import {
  getLatestPartyChatActivity,
  partyChatTimeMs,
} from "./party-chat";
import type { PartyChatMessage } from "./types";

export const PARTY_CHAT_LAST_READ_STORAGE_PREFIX = "party-chat-last-read";

export interface PartyChatReadStorageReader {
  getItem(key: string): string | null;
}

export interface PartyChatReadStorageWriter {
  setItem(key: string, value: string): void;
}

function normalizeId(value: string): string {
  return value.trim();
}

export function getPartyChatLastReadStorageKey(
  partyId: string,
  userId: string,
): string {
  return `${PARTY_CHAT_LAST_READ_STORAGE_PREFIX}:${normalizeId(partyId)}:${normalizeId(userId)}`;
}

export function getPartyChatLastReadAt(
  storage: PartyChatReadStorageReader,
  partyId: string,
  userId: string,
): string | null {
  const normalizedPartyId = normalizeId(partyId);
  const normalizedUserId = normalizeId(userId);
  if (!normalizedPartyId || !normalizedUserId) {
    return null;
  }

  const value = storage.getItem(
    getPartyChatLastReadStorageKey(normalizedPartyId, normalizedUserId),
  );
  if (!value) {
    return null;
  }

  return Number.isFinite(partyChatTimeMs(value)) ? value : null;
}

export function markPartyChatLastRead(
  storage: PartyChatReadStorageWriter & PartyChatReadStorageReader,
  partyId: string,
  userId: string,
  readAt: string,
): void {
  const normalizedPartyId = normalizeId(partyId);
  const normalizedUserId = normalizeId(userId);
  if (!normalizedPartyId || !normalizedUserId) {
    return;
  }

  const nextMs = partyChatTimeMs(readAt);
  if (!Number.isFinite(nextMs)) {
    return;
  }

  const current = getPartyChatLastReadAt(
    storage,
    normalizedPartyId,
    normalizedUserId,
  );
  if (current && partyChatTimeMs(current) >= nextMs) {
    return;
  }

  storage.setItem(
    getPartyChatLastReadStorageKey(normalizedPartyId, normalizedUserId),
    readAt,
  );
}

export function markPartyChatReadFromMessages(
  storage: PartyChatReadStorageWriter & PartyChatReadStorageReader,
  partyId: string,
  userId: string,
  messages: readonly PartyChatMessage[],
  fallbackReadAt = new Date().toISOString(),
): void {
  const latest = getLatestPartyChatActivity(messages);
  markPartyChatLastRead(storage, partyId, userId, latest?.at ?? fallbackReadAt);
}
